// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal";
import TextArea from "@insite/mobius/TextArea";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import Block from "@store/Library/Components/Toolkit/Block";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import submitForm from "@store/Framework/Store/Store/Pages/Crm/Handlers/SubmitCrmForm";
import { FormFieldModel } from "@store/Framework/Types/ApiModels";
import CustomerInformationFields, {
    CustomerFormErrors,
    CustomerFormState,
    emptyCustomerFormState,
    toCustomerInformationModel,
} from "@store/Library/Components/Forms/CustomerInformationFields";
import { configurationFormProps } from "@store/Themes/common/props/configuratorForm";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, FormEvent, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapDispatchToProps = {
    submitForm,
};

const mapStateToProps = (state: ApplicationState) => {
    const v = state.store.data.valveConfigurator;
    const seriesLabel =
        v.selectors?.valveTypes?.find(t => t.code === v.selectedSeries)?.label ?? v.selectedSeries ?? "";
    return {
        valveType: seriesLabel,
        valveSize: v.selectedSize !== null && v.selectedSize !== undefined ? String(v.selectedSize) : "",
        flangeDrilling: v.flangeDrillingLabel ?? "",
        actuator: v.selectedActuator ?? "",
        gateMaterial: v.selectedGateMaterial ?? "",
        elastomer: v.selectedElastomer ?? "",
        readOnlyFields: v.readOnlyFields,
    };
};

interface OwnProps {
    partNumber: string;
    modelNumber?: string;
    partsListNumber?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = configurationFormProps;

// The quote form is embedded inside the already-bordered summary card, so strip the
// container's own border/padding to avoid a double outline.
const formContainer = {
    ...styles.formContainer,
    css: css`
        padding: 0;
    `,
};

const Spacer = styled.div`
    width: 100%;
    margin-bottom: ${pxToRem(12)};
    border-bottom: 1px solid ${getColor("common.border")};
`;

const ConfigSummary = styled.dl`
    margin: 0 0 ${pxToRem(4)};
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: ${pxToRem(12)};
    row-gap: ${pxToRem(4)};

    dt {
        margin: 0;
        font-weight: 600;
        color: ${getColor("text.main")};
        white-space: nowrap;
    }

    dd {
        margin: 0;
        color: ${getColor("text.main")};
        word-break: break-word;
    }
`;

const ValveConfiguratorQuoteForm: FC<Props> = ({
    partNumber,
    modelNumber,
    partsListNumber,
    valveType,
    valveSize,
    flangeDrilling,
    actuator,
    gateMaterial,
    elastomer,
    readOnlyFields,
    submitForm,
}) => {
    const [form, setForm] = useState<CustomerFormState>(emptyCustomerFormState());
    const [comment, setComment] = useState("");
    const [errors, setErrors] = useState<CustomerFormErrors>({});
    const [customerErrors, setCustomerErrors] = useState<CustomerFormErrors>({});
    const [reloadCustomerInformationFields, setReloadCustomerInformationFields] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const handleChange =
        (fieldName: keyof CustomerFormState) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const value = event.target.value;
            setForm(prev => ({ ...prev, [fieldName]: value }));
            if (errors[fieldName]) {
                setErrors(prev => ({ ...prev, [fieldName]: undefined }));
            }
            setReloadCustomerInformationFields(false);
        };

    const toFormFields = (): FormFieldModel[] =>
        [
            { name: "Part Number", value: partNumber },
            { name: "Model Number", value: modelNumber || "" },
            { name: "Parts List Number", value: partsListNumber || "" },
            { name: "Valve Type", value: valveType },
            { name: "Valve Size", value: valveSize },
            { name: "Flange Drilling", value: flangeDrilling },
            { name: "Actuator", value: actuator },
            { name: "Gate Material", value: gateMaterial },
            { name: "Wear Sleeve Elastomer", value: elastomer },
            { name: "Valve Standard", value: readOnlyFields?.valveStandard || "" },
            { name: "Gate Coating", value: readOnlyFields?.gateCoating?.value || "" },
            { name: "Paint", value: readOnlyFields?.paint?.value || "" },
            { name: "Valve Materials", value: readOnlyFields?.valveMaterials?.value || "" },
            { name: "Hardware Material", value: readOnlyFields?.hardwareMaterial?.value || "" },
            { name: "Additional Details", value: comment.trim(), format: "long" as const },
        ].filter(field => !!field.value);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        setSubmitError(undefined);

        const nextErrors = { ...customerErrors };
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        setIsSubmitting(true);
        submitForm({
            formType: "ValveConfigurator",
            formFields: toFormFields(),
            customerInformation: toCustomerInformationModel(form),
            onSuccess: () => setIsSuccessModalOpen(true),
            onError: (error: string) => setSubmitError(error),
            onComplete: () => setIsSubmitting(false),
        });
    };

    const closeSuccessModal = () => {
        setIsSuccessModalOpen(false);
        setForm(emptyCustomerFormState());
        setComment("");
        setErrors({});
        setSubmitError(undefined);
        setReloadCustomerInformationFields(true);
    };

    return (
        <Block {...styles.block}>
            <FormWrapper generalErrorMessage={submitError}>
                <LoadingOverlay {...styles.loadingOverlay} loading={isSubmitting}>
                    <GridContainer {...formContainer} as="form" onSubmit={handleSubmit}>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.titleText}>
                                {translate("FlsValveConfigurator.Quote.Title")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Spacer />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.introText}>
                                {translate("FlsValveConfigurator.Quote.Intro")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <ConfigSummary>
                                <dt>{translate("FlsValveConfigurator.Bom.PartNo")}</dt>
                                <dd>{partNumber || "—"}</dd>
                                {modelNumber ? (
                                    <>
                                        <dt>{translate("FlsValveConfigurator.Bom.ModelNo")}</dt>
                                        <dd>{modelNumber}</dd>
                                    </>
                                ) : null}
                                {partsListNumber ? (
                                    <>
                                        <dt>{translate("FlsValveConfigurator.Bom.PartsListNo")}</dt>
                                        <dd>{partsListNumber}</dd>
                                    </>
                                ) : null}
                            </ConfigSummary>
                        </GridItem>
                        <CustomerInformationFields
                            {...{
                                form,
                                updateCustomerState: (customerState: CustomerFormState) => {
                                    setForm(prev => ({ ...prev, ...customerState }));
                                },
                                onHandleChange: handleChange,
                                onValidationChange: (
                                    nextCustomerErrors: Partial<Record<keyof CustomerFormState, string>>,
                                ) => {
                                    setCustomerErrors(() => ({ ...nextCustomerErrors }));
                                },
                                errors,
                                reloadCustomerInformationFields,
                            }}
                        />
                        <GridItem {...styles.fullWidthItem}>
                            <TextArea
                                label={translate("FlsValveConfigurator.Quote.CommentLabel")}
                                placeholder={translate("FlsValveConfigurator.Quote.CommentPlaceholder")}
                                value={comment}
                                onChange={event => setComment(event.target.value)}
                                rows={4}
                                data-test-selector="valveQuote_comment"
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Spacer />
                        </GridItem>
                        <GridItem {...styles.actionItem}>
                            <FormActions {...styles.formActions}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || isSuccessModalOpen}
                                    data-test-selector="valveQuote_submit"
                                >
                                    {isSubmitting
                                        ? translate("Submitting...")
                                        : translate("FlsValveConfigurator.Quote.Submit")}
                                </Button>
                            </FormActions>
                        </GridItem>
                    </GridContainer>
                    <Modal
                        headline={translate("Request sent")}
                        contentLabel={translate("Your quote request has been submitted.")}
                        {...styles.successModal}
                        isOpen={isSuccessModalOpen}
                        handleClose={closeSuccessModal}
                    >
                        <InfosBox {...styles.successInfosBox}>
                            {translate("Your quote request has been submitted. A representative will contact you shortly.")}
                        </InfosBox>
                        <FormActions>
                            <Button
                                {...styles.continueButton}
                                onClick={closeSuccessModal}
                                data-test-selector="valveQuote_continue"
                            >
                                {translate("Continue")}
                            </Button>
                        </FormActions>
                    </Modal>
                </LoadingOverlay>
            </FormWrapper>
        </Block>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ValveConfiguratorQuoteForm);
